/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import { task } from 'ember-concurrency';
import FormValidations from 'cio/utils/form-validations';

import AlertsService from 'cio/services/alerts';
import CategoryModel from 'cio/models/category';
import SavedRowModel from 'cio/models/saved-row';
import ArrayProxy from '@ember/array/proxy';
import DS from 'ember-data';
import ApiErrorsService from 'cio/services/api-errors';
import { CategoryOption } from 'cio/components/category-dropdown-item';
import isDestroyed from 'cio/utils/is-destroyed';

interface DragAndDropSaveRowModalArgs {
  existingSavedRow?: SavedRowModel;
  onCancel: () => unknown;
  onSave: (row: SavedRowModel) => unknown;
  onCreateCategory: (category: CategoryModel) => unknown;
  hasReadOnlyPermission: boolean;
}

const MAX_CATEGORIES_REQUEST_ATTEMPTS = 3;

const RESERVED_CATEGORY_NAMES = ['empty', 'default', 'all custom rows', 'uncategorized custom rows'];

const Validations = {
  name(value: string | undefined) {
    if (isEmpty(value?.trim())) {
      return "Name can't be blank";
    }

    return true;
  },
};

export default class DragAndDropSaveRowModal extends Component<DragAndDropSaveRowModalArgs> {
  validations!: FormValidations;

  @service alerts!: AlertsService;
  @service apiErrors!: ApiErrorsService;
  @service store!: DS.Store;

  @tracked categories: CategoryModel[] | ArrayProxy<CategoryModel> = [];
  @tracked selectedCategory = undefined;
  @tracked categoriesFetchAttempts = 0;
  @tracked categoryUpdateError?: string = undefined;
  @tracked row!: SavedRowModel;
  @tracked canUpdateRow = false;

  get hasCategoryUpdateError() {
    return isPresent(this.categoryUpdateError);
  }

  get categoryOptions() {
    if (this.fetchCategoriesTask.isRunning) {
      return undefined;
    }

    return this.categories.map((category) => ({ value: category.name, label: category.name, model: category }));
  }

  get hasActiveCategoryRequest() {
    return this.fetchCategoriesTask.isRunning || this.updateCategoryTask.isRunning || this.createCategoryTask.isRunning;
  }

  get categoryFieldDisabled() {
    return this.args.hasReadOnlyPermission || this.hasActiveCategoryRequest;
  }

  get isSaving() {
    return this.saveTask.isRunning || this.saveAsNewTask.isRunning;
  }

  get saveDisabled() {
    return (
      this.args.hasReadOnlyPermission || this.hasActiveCategoryRequest || this.isSaving || this.validations.hasErrors
    );
  }

  get saveAsNewDisabled() {
    return this.saveDisabled || !this.row.hasDirtyAttributes;
  }

  constructor(owner: unknown, args: DragAndDropSaveRowModalArgs) {
    super(owner, args);

    this.fetchCategoriesTask.perform();

    if (isPresent(this.args.existingSavedRow)) {
      this.row = this.args.existingSavedRow as SavedRowModel;
      this.canUpdateRow = true;
    } else {
      this.row = this.store.createRecord('saved-row');
    }

    this.validations = FormValidations.create({
      validations: Validations,
      context: this,
      modelKey: 'row',
    });
  }

  willDestroy() {
    super.willDestroy();

    if (isDestroyed(this.row)) {
      // can happen in tests
      return;
    }

    if (this.row.get('isNew')) {
      this.row.unloadRecord();
    } else {
      this.row.rollbackAttributes();
    }
  }

  notifySaveSuccess() {
    this.alerts.success('savedRow.save.success', 'Your row will be available in the Rows tab in the sidebar');
  }

  fetchCategoriesTask = task(async () => {
    try {
      let categories = await this.store.findAll('category');
      this.categories = categories;
    } catch {
      // try to load the categories records up to 3 times
      if (this.categoriesFetchAttempts < MAX_CATEGORIES_REQUEST_ATTEMPTS) {
        this.fetchCategoriesTask.perform();
        this.categoriesFetchAttempts = this.categoriesFetchAttempts + 1;
      } else {
        this.args.onCancel();
        this.alerts.error(
          'categories.request.failure',
          'Unable to load Saved Row Categories. Please try again or contact support for help.',
        );
      }
    }
  });

  createCategoryTask = task(async (name: string) => {
    let category = this.store.createRecord('category', { name });

    try {
      return await category.save();
    } catch (e) {
      category.rollbackAttributes();
      throw e;
    }
  });

  updateCategoryTask = task(async (option: CategoryOption) => {
    let category: CategoryModel | undefined = option?.model;

    this.categoryUpdateError = undefined;

    if (option?.__customvalue__) {
      if (RESERVED_CATEGORY_NAMES.includes(option.value!.toLowerCase())) {
        this.categoryUpdateError = 'Category name is reserved. Please try a different name.';
      } else {
        try {
          category = await this.createCategoryTask.perform(option.value);
          this.args.onCreateCategory(category);
        } catch (reason) {
          let error = reason.errors?.[0]?.detail;
          this.categoryUpdateError =
            error ?? 'Unable to create Category. Please try again or contact support for help.';
        }
      }
    }

    return this.row.set('category', category);
  });

  saveAsNewTask = task(async () => {
    let { name, category } = this.row;

    this.row.rollbackAttributes();

    this.row = this.store.createRecord('saved-row', { name, category });

    await this.saveTask.perform();

    this.canUpdateRow = false;
  });

  saveTask = task(async () => {
    try {
      await this.validations.validateModel();
      await this.args.onSave(this.row);

      this.notifySaveSuccess();
    } catch (reason) {
      this.apiErrors.report(reason);
    }

    return this.row;
  });

  @action
  save(e: Event) {
    e.preventDefault();
    this.saveTask.perform();
  }

  @action
  onNameChange(name: string) {
    this.row.name = name;
    this.validations.validateAttribute('name');
  }
}
